/**
 * Platform-administrator Cognitory registry and bounded create at /admin/cogs.
 */

import {
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useState,
} from "react";
import {
  executePlatformAdministratorInitialOwner,
  planPlatformAdministratorInitialOwner,
  type InitialOwnerAssignmentPlan,
  type InitialOwnerSelection,
} from "../api/cognitoryPlatformAdminInitialOwnerClient";
import {
  executePlatformAdministratorSystemCreate,
  planPlatformAdministratorSystemCreate,
  type PlatformAdministratorCreatePlan,
} from "../api/cognitoryPlatformAdminCreateClient";
import { useCognitoryAuth } from "../auth/useCognitoryAuth";
import {
  usePlatformAdministratorCognitories,
  type PlatformAdministratorCognitoriesPhase,
} from "../entry/usePlatformAdministratorCognitories";
import { buildCanonicalProvisioningSetupPath } from "../presentation/cognitoryProvisioningSetupNavigation";
import { buildCanonicalCognitoryWorkspacePath } from "../presentation/cognitoryWorkspaceNavigation";
import PlatformAdministratorMembersPanel from "./PlatformAdministratorMembersPanel";
import FusionSun from "./FusionSun";
import SiteDeploymentStamp from "./SiteDeploymentStamp";
import {
  MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE,
  MAIN_PAGE_BODY_STYLE,
  MAIN_PAGE_COLORS,
  MAIN_PAGE_CONTENT_Z_INDEX,
  MAIN_PAGE_FONT_STACK,
  MAIN_PAGE_LIQUID_BUTTON_STYLE,
  MAIN_PAGE_PANEL_INNER_STYLE,
  MAIN_PAGE_PANEL_STYLE,
  MAIN_PAGE_QUIET_BUTTON_STYLE,
  MAIN_PAGE_RADIUS,
  MAIN_PAGE_SECTION_LABEL_STYLE,
  bindMainPageLiquidButtonInteractions,
  mainPageLiquidButtonHandlers,
} from "../theme/mainPageVisual";

const HERO_EXPLANATORY_COPY =
  "Create Cognitories for this platform and open those you are authorized to use.";

function phaseStatusLabel(
  phase: PlatformAdministratorCognitoriesPhase,
): string {
  switch (phase) {
    case "loading":
      return "Loading your Cognitories…";
    case "empty":
      return "No Cognitories in the platform registry yet.";
    case "one":
      return "One Cognitory in the platform registry.";
    case "many":
      return "Platform Cognitory registry.";
    case "unauthorized":
      return "You do not have platform administrator access.";
    case "temporary_failure":
      return "Unable to load Cognitories right now.";
    case "unable_to_verify":
      return "Unable to verify the Cognitory registry.";
    default:
      return "Sign in to view platform Cognitories.";
  }
}

function AdminCogsPageShell({ children }: { children: ReactNode }) {
  return (
    <div
      data-cognitory-visual-shell="main-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        isolation: "isolate",
        background: MAIN_PAGE_COLORS.pageBackground,
        fontFamily: MAIN_PAGE_FONT_STACK,
      }}
    >
      <FusionSun />
      <div
        style={{
          position: "relative",
          zIndex: MAIN_PAGE_CONTENT_Z_INDEX,
          flex: "1 0 auto",
          width: "100%",
          maxWidth: "720px",
          margin: "0 auto",
          padding:
            "clamp(1.75rem, 4vw, 3rem) clamp(1.25rem, 4vw, 2rem) clamp(2rem, 4vw, 3rem)",
          boxSizing: "border-box",
        }}
      >
        <header
          data-testid="platform-admin-cogs-hero"
          style={{
            textAlign: "center",
            marginBottom: "clamp(1.25rem, 3vw, 1.75rem)",
          }}
        >
          <div style={MAIN_PAGE_SECTION_LABEL_STYLE}>
            PLATFORM ADMINISTRATION
          </div>
          <h1
            data-testid="platform-admin-cogs-title"
            style={{
              margin: "0.5rem auto 0.85rem",
              maxWidth: "18ch",
              fontWeight: 300,
              letterSpacing: "-0.03em",
              fontFamily: MAIN_PAGE_FONT_STACK,
              fontSize: "clamp(2rem, 4.2vw, 3rem)",
              lineHeight: 1.15,
              color: MAIN_PAGE_COLORS.ink,
              textWrap: "balance",
            }}
          >
            Your Cognitories
          </h1>
          <p
            data-testid="platform-admin-cogs-hero-copy"
            style={{
              fontSize: "clamp(1rem, 1.6vw, 1.12rem)",
              color: MAIN_PAGE_COLORS.muted,
              margin: "0 auto",
              maxWidth: "32rem",
              fontWeight: 300,
              lineHeight: 1.65,
            }}
          >
            {HERO_EXPLANATORY_COPY}
          </p>
        </header>
        <div style={MAIN_PAGE_PANEL_STYLE}>
          <div style={MAIN_PAGE_PANEL_INNER_STYLE}>{children}</div>
        </div>
      </div>
      <div
        style={{
          position: "relative",
          zIndex: MAIN_PAGE_CONTENT_Z_INDEX,
          padding: "0 1rem 1.5rem",
        }}
      >
        <SiteDeploymentStamp />
      </div>
    </div>
  );
}

function SignInErrorBanner(input: { message: string; onDismiss: () => void }) {
  return (
    <p
      data-testid="platform-admin-cogs-sign-in-error"
      role="alert"
      style={{
        color: MAIN_PAGE_COLORS.alertInk,
        fontSize: "0.95rem",
        marginTop: "0.9rem",
        marginBottom: 0,
        padding: "0.65rem 0.75rem",
        borderRadius: MAIN_PAGE_RADIUS.alert,
        background: MAIN_PAGE_COLORS.alertFill,
        border: `1px solid ${MAIN_PAGE_COLORS.alertBorder}`,
        fontFamily: MAIN_PAGE_FONT_STACK,
      }}
    >
      {input.message}{" "}
      <button
        type="button"
        data-testid="platform-admin-cogs-sign-in-error-dismiss"
        onClick={input.onDismiss}
        style={{
          border: "none",
          background: "transparent",
          color: MAIN_PAGE_COLORS.link,
          textDecoration: "underline",
          cursor: "pointer",
          padding: 0,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        Dismiss
      </button>
    </p>
  );
}

function GoogleSignInButton(input: { onSignIn: () => Promise<void> }) {
  return (
    <button
      type="button"
      data-testid="platform-admin-cogs-sign-in"
      aria-label="Sign in with Google"
      style={MAIN_PAGE_LIQUID_BUTTON_STYLE}
      {...mainPageLiquidButtonHandlers}
      onClick={() => {
        void input.onSignIn();
      }}
    >
      Sign in with Google
    </button>
  );
}

const adminOpenLinkHandlers = {
  onFocus: (event: FocusEvent<HTMLAnchorElement>) => {
    bindMainPageLiquidButtonInteractions(
      event.currentTarget as unknown as HTMLButtonElement,
      "focus",
    );
  },
  onBlur: (event: FocusEvent<HTMLAnchorElement>) => {
    bindMainPageLiquidButtonInteractions(
      event.currentTarget as unknown as HTMLButtonElement,
      "blur",
    );
  },
  onMouseEnter: (event: MouseEvent<HTMLAnchorElement>) => {
    bindMainPageLiquidButtonInteractions(
      event.currentTarget as unknown as HTMLButtonElement,
      "enter",
    );
  },
  onMouseLeave: (event: MouseEvent<HTMLAnchorElement>) => {
    bindMainPageLiquidButtonInteractions(
      event.currentTarget as unknown as HTMLButtonElement,
      "leave",
    );
  },
  onMouseDown: (event: MouseEvent<HTMLAnchorElement>) => {
    bindMainPageLiquidButtonInteractions(
      event.currentTarget as unknown as HTMLButtonElement,
      "down",
    );
  },
  onMouseUp: (event: MouseEvent<HTMLAnchorElement>) => {
    bindMainPageLiquidButtonInteractions(
      event.currentTarget as unknown as HTMLButtonElement,
      "up",
    );
  },
};

function SystemSummaryRow(input: {
  systemId: string;
  displayName: string;
  status: string;
  enterable: boolean;
  canOpen: boolean;
  canSetup: boolean;
  initialOwnerAssignmentStatus: "unassigned" | "assigned" | "unavailable";
  onRegistryRefresh: () => void;
}) {
  const workspacePath = input.canOpen
    ? buildCanonicalCognitoryWorkspacePath(input.systemId)
    : null;
  const provisioningSetupPath = input.canSetup
    ? buildCanonicalProvisioningSetupPath(input.systemId)
    : null;

  return (
    <li
      data-testid="platform-admin-cognitory-row"
      data-system-id={input.systemId}
      style={{
        listStyle: "none",
        padding: "1rem 1.1rem",
        borderRadius: MAIN_PAGE_RADIUS.panel,
        border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: MAIN_PAGE_COLORS.quietFill,
        textAlign: "left",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div
        style={{
          fontWeight: 500,
          fontSize: "1.02rem",
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
          lineHeight: 1.35,
        }}
      >
        {input.displayName}
      </div>
      <div
        style={{
          marginTop: "0.55rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "0.45rem",
        }}
      >
        <span
          style={{
            fontSize: "0.82rem",
            padding: "0.2rem 0.55rem",
            borderRadius: MAIN_PAGE_RADIUS.capsule,
            border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
            color: MAIN_PAGE_COLORS.slate,
            background: "rgba(255, 255, 255, 0.72)",
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Status: {input.status}
        </span>
        <span
          style={{
            fontSize: "0.82rem",
            padding: "0.2rem 0.55rem",
            borderRadius: MAIN_PAGE_RADIUS.capsule,
            border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
            color: input.enterable
              ? MAIN_PAGE_COLORS.slate
              : MAIN_PAGE_COLORS.soft,
            background: "rgba(255, 255, 255, 0.72)",
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Enterable: {input.enterable ? "Yes" : "No"}
        </span>
      </div>
      <div
        style={{
          marginTop: "0.55rem",
          fontSize: "0.82rem",
          color: MAIN_PAGE_COLORS.whisper,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        System ID: {input.systemId}
      </div>
      {input.status === "provisioning" &&
      input.initialOwnerAssignmentStatus === "unassigned" ? (
        <AssignInitialOwnerPanel
          systemId={input.systemId}
          displayName={input.displayName}
          onSuccess={input.onRegistryRefresh}
        />
      ) : input.initialOwnerAssignmentStatus === "assigned" ? (
        <p
          data-testid="platform-admin-initial-owner-assigned"
          style={{
            marginTop: "0.75rem",
            marginBottom: 0,
            fontSize: "0.9rem",
            color: MAIN_PAGE_COLORS.soft,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Initial owner assigned.
        </p>
      ) : input.initialOwnerAssignmentStatus === "unavailable" ? (
        <p
          data-testid="platform-admin-initial-owner-unavailable"
          style={{
            marginTop: "0.75rem",
            marginBottom: 0,
            fontSize: "0.9rem",
            color: MAIN_PAGE_COLORS.soft,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          Owner assignment unavailable.
        </p>
      ) : null}
      {input.canSetup && provisioningSetupPath ? (
        <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
          <a
            href={provisioningSetupPath}
            data-testid="platform-admin-cognitory-begin-setup"
            data-system-id={input.systemId}
            style={MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE}
            {...adminOpenLinkHandlers}
          >
            Begin setup
          </a>
        </div>
      ) : null}
      <PlatformAdministratorMembersPanel systemId={input.systemId} />
      {input.canOpen && workspacePath ? (
        <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
          <a
            href={workspacePath}
            data-testid="platform-admin-cognitory-open"
            data-system-id={input.systemId}
            style={MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE}
            {...adminOpenLinkHandlers}
          >
            Open Cognitory
          </a>
        </div>
      ) : input.enterable ? (
        <div
          data-testid="platform-admin-cognitory-no-access"
          data-system-id={input.systemId}
          style={{
            marginTop: "0.75rem",
            textAlign: "center",
            fontSize: "0.9rem",
            color: MAIN_PAGE_COLORS.soft,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          No user access
        </div>
      ) : null}
    </li>
  );
}

function ReturnHomeLink() {
  return (
    <div style={{ textAlign: "center", marginTop: "1rem" }}>
      <a href="/" style={{ color: MAIN_PAGE_COLORS.link }}>
        Return home
      </a>
    </div>
  );
}

type AssignInitialOwnerPhase = "closed" | "choose" | "review" | "submitting";

function AssignInitialOwnerPanel(input: {
  systemId: string;
  displayName: string;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<AssignInitialOwnerPhase>("choose");
  const [ownerSelection, setOwnerSelection] =
    useState<InitialOwnerSelection | null>(null);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [plan, setPlan] = useState<InitialOwnerAssignmentPlan | null>(null);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase("choose");
    setOwnerSelection(null);
    setOwnerEmail("");
    setPlan(null);
    setConfirmChecked(false);
    setErrorMessage(null);
  }, []);

  const handlePlan = useCallback(async () => {
    if (!ownerSelection) {
      setErrorMessage("Choose who will be the initial owner.");
      return;
    }
    if (ownerSelection === "other" && ownerEmail.trim().length === 0) {
      setErrorMessage("Enter the verified email for the other user.");
      return;
    }
    setErrorMessage(null);
    setPhase("submitting");
    const result = await planPlatformAdministratorInitialOwner({
      systemId: input.systemId,
      ownerSelection,
      ...(ownerSelection === "other" ? { ownerEmail } : {}),
    });
    if (result.kind === "planned") {
      setPlan(result.plan);
      setConfirmChecked(false);
      setPhase("review");
      return;
    }
    setPhase("choose");
    if (result.kind === "already_assigned") {
      input.onSuccess();
      setOpen(false);
      reset();
      return;
    }
    if (result.kind === "owner_identity_unverified") {
      setErrorMessage("That account is missing or its email is not verified.");
    } else if (result.kind === "initial_owner_conflict") {
      setErrorMessage("Initial owner assignment is no longer available.");
    } else {
      setErrorMessage("Unable to prepare the assignment plan. Try again.");
    }
  }, [input, ownerEmail, ownerSelection, reset]);

  const handleExecute = useCallback(async () => {
    if (!plan || !confirmChecked || !ownerSelection) {
      return;
    }
    setErrorMessage(null);
    setPhase("submitting");
    const result = await executePlatformAdministratorInitialOwner({
      systemId: plan.systemId,
      ownerSelection,
      ...(ownerSelection === "other" ? { ownerEmail } : {}),
      planTimestamp: plan.planTimestamp,
      writeSetHash: plan.writeSetHash,
      confirmAssignInitialOwner: true,
    });
    if (result.kind === "assigned" || result.kind === "already_assigned") {
      input.onSuccess();
      setOpen(false);
      reset();
      return;
    }
    setPhase("review");
    if (result.kind === "validation_failed") {
      setErrorMessage("The assignment plan expired or changed. Start over.");
      setPlan(null);
      setPhase("choose");
    } else if (result.kind === "initial_owner_conflict") {
      setErrorMessage("Initial owner assignment is no longer available.");
    } else {
      setErrorMessage(
        "Unable to assign the initial owner right now. Try again.",
      );
    }
  }, [confirmChecked, input, ownerEmail, ownerSelection, plan, reset]);

  if (!open) {
    return (
      <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
        <button
          type="button"
          data-testid="platform-admin-initial-owner-open"
          style={MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE}
          onClick={() => {
            reset();
            setOpen(true);
          }}
        >
          Assign initial owner
        </button>
      </div>
    );
  }

  const disabled = phase === "submitting";
  const fieldStyle = {
    width: "100%",
    boxSizing: "border-box" as const,
    padding: "0.65rem 0.75rem",
    borderRadius: MAIN_PAGE_RADIUS.alert,
    border: `1px solid ${MAIN_PAGE_COLORS.inputBorder}`,
    fontFamily: MAIN_PAGE_FONT_STACK,
    fontSize: "0.95rem",
    marginTop: "0.35rem",
  };

  return (
    <div
      data-testid="platform-admin-initial-owner-panel"
      style={{
        marginTop: "0.75rem",
        padding: "0.85rem",
        borderRadius: MAIN_PAGE_RADIUS.panel,
        border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: "rgba(255, 255, 255, 0.55)",
      }}
    >
      {phase === "review" && plan ? (
        <div data-testid="platform-admin-initial-owner-review">
          <dl
            style={{
              margin: "0 0 0.85rem",
              fontSize: "0.9rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            <dt style={{ fontWeight: 500 }}>Cognitory</dt>
            <dd style={{ margin: "0.15rem 0 0.65rem" }}>{input.displayName}</dd>
            <dt style={{ fontWeight: 500 }}>System ID</dt>
            <dd style={{ margin: "0.15rem 0 0.65rem" }}>{plan.systemId}</dd>
            <dt style={{ fontWeight: 500 }}>Initial owner</dt>
            <dd style={{ margin: "0.15rem 0 0.65rem" }}>
              {plan.maskedOwnerEmail}
            </dd>
            <dt style={{ fontWeight: 500 }}>Role</dt>
            <dd style={{ margin: "0.15rem 0 0.65rem" }}>{plan.role}</dd>
            <dt style={{ fontWeight: 500 }}>Status</dt>
            <dd style={{ margin: "0.15rem 0 0" }}>{plan.status}</dd>
          </dl>
          <label
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "flex-start",
              fontSize: "0.9rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="checkbox"
              data-testid="platform-admin-initial-owner-confirm"
              checked={confirmChecked}
              disabled={disabled}
              onChange={event => setConfirmChecked(event.target.checked)}
            />
            <span>I confirm this initial owner assignment.</span>
          </label>
        </div>
      ) : (
        <div data-testid="platform-admin-initial-owner-choose">
          <fieldset
            style={{
              border: "none",
              padding: 0,
              margin: 0,
            }}
          >
            <legend
              style={{
                fontSize: "0.9rem",
                color: MAIN_PAGE_COLORS.slate,
                fontFamily: MAIN_PAGE_FONT_STACK,
                marginBottom: "0.5rem",
              }}
            >
              Who should be the initial owner?
            </legend>
            <label
              style={{
                display: "flex",
                gap: "0.5rem",
                alignItems: "center",
                fontSize: "0.9rem",
                color: MAIN_PAGE_COLORS.slate,
                fontFamily: MAIN_PAGE_FONT_STACK,
                marginBottom: "0.45rem",
              }}
            >
              <input
                type="radio"
                name={`initial-owner-${input.systemId}`}
                data-testid="platform-admin-initial-owner-self"
                checked={ownerSelection === "self"}
                disabled={disabled}
                onChange={() => setOwnerSelection("self")}
              />
              Assign me as initial owner
            </label>
            <label
              style={{
                display: "flex",
                gap: "0.5rem",
                alignItems: "center",
                fontSize: "0.9rem",
                color: MAIN_PAGE_COLORS.slate,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
            >
              <input
                type="radio"
                name={`initial-owner-${input.systemId}`}
                data-testid="platform-admin-initial-owner-other"
                checked={ownerSelection === "other"}
                disabled={disabled}
                onChange={() => setOwnerSelection("other")}
              />
              Assign another verified user
            </label>
          </fieldset>
          {ownerSelection === "other" ? (
            <label
              style={{
                display: "block",
                marginTop: "0.75rem",
                fontSize: "0.9rem",
                color: MAIN_PAGE_COLORS.slate,
                fontFamily: MAIN_PAGE_FONT_STACK,
              }}
            >
              Verified email
              <input
                type="email"
                data-testid="platform-admin-initial-owner-email"
                value={ownerEmail}
                disabled={disabled}
                onChange={event => setOwnerEmail(event.target.value)}
                style={fieldStyle}
              />
            </label>
          ) : null}
        </div>
      )}

      {errorMessage ? (
        <p
          data-testid="platform-admin-initial-owner-error"
          role="alert"
          style={{
            marginTop: "0.75rem",
            marginBottom: 0,
            fontSize: "0.9rem",
            color: MAIN_PAGE_COLORS.alertInk,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          {errorMessage}
        </p>
      ) : null}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.65rem",
          justifyContent: "center",
          marginTop: "1rem",
        }}
      >
        {phase === "review" && plan ? (
          <button
            type="button"
            data-testid="platform-admin-initial-owner-execute"
            style={MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE}
            disabled={disabled || !confirmChecked}
            onClick={() => {
              void handleExecute();
            }}
          >
            Confirm assignment
          </button>
        ) : (
          <button
            type="button"
            data-testid="platform-admin-initial-owner-plan"
            style={MAIN_PAGE_ADMIN_OPEN_BUTTON_STYLE}
            disabled={disabled}
            onClick={() => {
              void handlePlan();
            }}
          >
            Review assignment
          </button>
        )}
        <button
          type="button"
          data-testid="platform-admin-initial-owner-cancel"
          style={MAIN_PAGE_QUIET_BUTTON_STYLE}
          disabled={disabled}
          onClick={() => {
            setOpen(false);
            reset();
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

type CreateFormPhase = "closed" | "editing" | "review" | "submitting";

function CreateCognitoryPanel(input: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [phase, setPhase] = useState<CreateFormPhase>("editing");
  const [systemId, setSystemId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [plan, setPlan] = useState<PlatformAdministratorCreatePlan | null>(
    null,
  );
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setPhase("editing");
    setPlan(null);
    setConfirmChecked(false);
    setErrorMessage(null);
  }, []);

  const handlePlan = useCallback(async () => {
    setErrorMessage(null);
    const trimmedId = systemId.trim();
    const trimmedName = displayName.trim();
    if (!trimmedId || !trimmedName) {
      setErrorMessage("System ID and display name are required.");
      return;
    }
    setPhase("submitting");
    const result = await planPlatformAdministratorSystemCreate({
      systemId: trimmedId,
      displayName: trimmedName,
    });
    if (result.kind !== "planned") {
      setPhase("editing");
      if (result.kind === "validation_failed") {
        setErrorMessage(
          "Check the System ID and display name, then try again.",
        );
      } else {
        setErrorMessage("Unable to prepare the create plan. Try again.");
      }
      return;
    }
    setPlan(result.plan);
    setConfirmChecked(false);
    setPhase("review");
  }, [displayName, systemId]);

  const handleExecute = useCallback(async () => {
    if (!plan || !confirmChecked) {
      return;
    }
    setErrorMessage(null);
    setPhase("submitting");
    const result = await executePlatformAdministratorSystemCreate({
      systemId: plan.systemId,
      displayName: plan.displayName,
      planTimestamp: plan.planTimestamp,
      writeSetHash: plan.writeSetHash,
      confirmCreate: true,
    });
    if (result.kind === "created" || result.kind === "already_exists") {
      input.onSuccess();
      input.onClose();
      return;
    }
    setPhase("review");
    if (result.kind === "validation_failed") {
      setErrorMessage("The create plan expired or changed. Start over.");
      setPlan(null);
      setPhase("editing");
    } else if (result.kind === "create_conflict") {
      setErrorMessage("This System ID already exists or cannot be created.");
    } else {
      setErrorMessage("Unable to create the Cognitory right now. Try again.");
    }
  }, [confirmChecked, input, plan]);

  const fieldStyle = {
    width: "100%",
    boxSizing: "border-box" as const,
    padding: "0.65rem 0.75rem",
    borderRadius: MAIN_PAGE_RADIUS.alert,
    border: `1px solid ${MAIN_PAGE_COLORS.inputBorder}`,
    fontFamily: MAIN_PAGE_FONT_STACK,
    fontSize: "0.95rem",
    marginTop: "0.35rem",
  };

  const disabled = phase === "submitting";

  return (
    <div
      data-testid="platform-admin-create-panel"
      style={{
        marginBottom: "1rem",
        padding: "1rem",
        borderRadius: MAIN_PAGE_RADIUS.panel,
        border: `1px solid ${MAIN_PAGE_COLORS.panelBorder}`,
        background: "rgba(255, 255, 255, 0.55)",
      }}
    >
      <h2
        style={{
          margin: "0 0 0.5rem",
          fontSize: "1.05rem",
          fontWeight: 500,
          color: MAIN_PAGE_COLORS.inkStrong,
          fontFamily: MAIN_PAGE_FONT_STACK,
        }}
      >
        Create Cognitory
      </h2>
      <p
        style={{
          margin: "0 0 0.85rem",
          fontSize: "0.9rem",
          color: MAIN_PAGE_COLORS.muted,
          fontFamily: MAIN_PAGE_FONT_STACK,
          lineHeight: 1.55,
        }}
      >
        This creates the Cognitory record only. It does not activate the
        Cognitory or grant anyone access.
      </p>

      {phase === "review" && plan ? (
        <div data-testid="platform-admin-create-review">
          <dl
            style={{
              margin: "0 0 0.85rem",
              fontSize: "0.9rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            <dt style={{ fontWeight: 500 }}>Display name</dt>
            <dd style={{ margin: "0.15rem 0 0.65rem" }}>{plan.displayName}</dd>
            <dt style={{ fontWeight: 500 }}>System ID</dt>
            <dd style={{ margin: "0.15rem 0 0.65rem" }}>{plan.systemId}</dd>
            <dt style={{ fontWeight: 500 }}>Status</dt>
            <dd style={{ margin: "0.15rem 0 0" }}>{plan.status}</dd>
          </dl>
          <label
            style={{
              display: "flex",
              gap: "0.5rem",
              alignItems: "flex-start",
              fontSize: "0.9rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          >
            <input
              type="checkbox"
              data-testid="platform-admin-create-confirm"
              checked={confirmChecked}
              disabled={disabled}
              onChange={event => setConfirmChecked(event.target.checked)}
            />
            <span>
              I confirm this creates the Cognitory record without activation or
              access grants.
            </span>
          </label>
        </div>
      ) : (
        <div data-testid="platform-admin-create-fields">
          <label
            style={{
              display: "block",
              fontSize: "0.9rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            Display name
            <input
              type="text"
              data-testid="platform-admin-create-display-name"
              value={displayName}
              disabled={disabled}
              onChange={event => setDisplayName(event.target.value)}
              style={fieldStyle}
            />
          </label>
          <label
            style={{
              display: "block",
              marginTop: "0.75rem",
              fontSize: "0.9rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            System ID
            <input
              type="text"
              data-testid="platform-admin-create-system-id"
              value={systemId}
              disabled={disabled}
              onChange={event => setSystemId(event.target.value)}
              style={fieldStyle}
            />
          </label>
        </div>
      )}

      {errorMessage ? (
        <p
          data-testid="platform-admin-create-error"
          role="alert"
          style={{
            marginTop: "0.75rem",
            marginBottom: 0,
            fontSize: "0.9rem",
            color: MAIN_PAGE_COLORS.alertInk,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          {errorMessage}
        </p>
      ) : null}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.65rem",
          justifyContent: "center",
          marginTop: "1rem",
        }}
      >
        {phase === "review" && plan ? (
          <button
            type="button"
            data-testid="platform-admin-create-execute"
            disabled={disabled || !confirmChecked}
            style={{
              ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
              marginTop: 0,
              opacity: disabled || !confirmChecked ? 0.65 : 1,
            }}
            {...mainPageLiquidButtonHandlers}
            onClick={() => {
              void handleExecute();
            }}
          >
            {disabled ? "Creating…" : "Create Cognitory"}
          </button>
        ) : (
          <button
            type="button"
            data-testid="platform-admin-create-plan"
            disabled={disabled}
            style={{
              ...MAIN_PAGE_LIQUID_BUTTON_STYLE,
              marginTop: 0,
              opacity: disabled ? 0.65 : 1,
            }}
            {...mainPageLiquidButtonHandlers}
            onClick={() => {
              void handlePlan();
            }}
          >
            {disabled ? "Preparing…" : "Review create plan"}
          </button>
        )}
        <button
          type="button"
          data-testid="platform-admin-create-cancel"
          disabled={disabled}
          style={MAIN_PAGE_QUIET_BUTTON_STYLE}
          onClick={() => {
            resetForm();
            input.onClose();
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function PlatformAdministratorCognitoriesPage() {
  const auth = useCognitoryAuth();
  const signedIn = auth.status === "signed_in";
  const registry = usePlatformAdministratorCognitories({ signedIn });
  const [createOpen, setCreateOpen] = useState(false);

  if (auth.status === "loading") {
    return (
      <AdminCogsPageShell>
        <div
          data-testid="platform-admin-cogs-loading-auth"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p style={{ textAlign: "center", marginBottom: 0 }}>
            Checking sign-in status…
          </p>
        </div>
      </AdminCogsPageShell>
    );
  }

  if (!signedIn) {
    return (
      <AdminCogsPageShell>
        <div
          data-testid="platform-admin-cogs-signed-out"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            style={{
              fontSize: "1rem",
              marginBottom: "0.55rem",
              color: MAIN_PAGE_COLORS.slate,
              fontFamily: MAIN_PAGE_FONT_STACK,
            }}
          >
            Sign in with a platform administrator Google account to view the
            read-only Cognitory registry.
          </p>
          {auth.errorMessage ? (
            <SignInErrorBanner
              message={auth.errorMessage}
              onDismiss={auth.clearError}
            />
          ) : null}
          <GoogleSignInButton onSignIn={auth.signIn} />
        </div>
      </AdminCogsPageShell>
    );
  }

  if (registry.phase === "loading") {
    return (
      <AdminCogsPageShell>
        <div
          data-testid="platform-admin-cogs-loading"
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="platform-admin-cogs-status"
            style={{ textAlign: "center", marginBottom: 0 }}
          >
            {phaseStatusLabel("loading")}
          </p>
        </div>
      </AdminCogsPageShell>
    );
  }

  if (
    registry.phase === "unauthorized" ||
    registry.phase === "temporary_failure" ||
    registry.phase === "unable_to_verify"
  ) {
    const isUnauthorized = registry.phase === "unauthorized";
    return (
      <AdminCogsPageShell>
        <div
          data-testid={
            isUnauthorized
              ? "platform-admin-cogs-unauthorized"
              : "platform-admin-cogs-error"
          }
          style={MAIN_PAGE_BODY_STYLE}
        >
          <p
            data-testid="platform-admin-cogs-status"
            style={{ textAlign: "center", marginBottom: "0.5rem" }}
          >
            {phaseStatusLabel(registry.phase)}
          </p>
          {isUnauthorized ? (
            <>
              <p
                data-testid="platform-admin-cogs-email"
                style={{
                  textAlign: "center",
                  fontSize: "0.95rem",
                  color: MAIN_PAGE_COLORS.slate,
                  fontFamily: MAIN_PAGE_FONT_STACK,
                  marginBottom: "0.75rem",
                }}
              >
                Signed in as {auth.email || "unknown"}
              </p>
              <button
                type="button"
                data-testid="platform-admin-cogs-sign-out"
                style={MAIN_PAGE_QUIET_BUTTON_STYLE}
                onClick={() => {
                  void auth.signOutUser();
                }}
              >
                Sign out and use another account
              </button>
            </>
          ) : (
            <>
              {registry.requestId ? (
                <p
                  data-testid="platform-admin-cogs-request-id"
                  style={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    color: MAIN_PAGE_COLORS.whisper,
                    fontFamily: MAIN_PAGE_FONT_STACK,
                  }}
                >
                  Request ID: {registry.requestId}
                </p>
              ) : null}
              <div style={{ textAlign: "center" }}>
                <button
                  type="button"
                  data-testid="platform-admin-cogs-retry"
                  style={MAIN_PAGE_QUIET_BUTTON_STYLE}
                  onClick={registry.retry}
                >
                  Try again
                </button>
              </div>
            </>
          )}
          <ReturnHomeLink />
        </div>
      </AdminCogsPageShell>
    );
  }

  return (
    <AdminCogsPageShell>
      <div
        data-testid={
          registry.phase === "empty"
            ? "platform-admin-cogs-empty"
            : registry.phase === "one"
              ? "platform-admin-cogs-one"
              : "platform-admin-cogs-many"
        }
        style={MAIN_PAGE_BODY_STYLE}
      >
        <p
          data-testid="platform-admin-cogs-status"
          style={{ textAlign: "center", marginBottom: "0.85rem" }}
        >
          {phaseStatusLabel(registry.phase)}
        </p>

        <div style={{ textAlign: "center", marginBottom: "0.85rem" }}>
          {!createOpen ? (
            <button
              type="button"
              data-testid="platform-admin-create-open"
              style={{ ...MAIN_PAGE_LIQUID_BUTTON_STYLE, marginTop: 0 }}
              {...mainPageLiquidButtonHandlers}
              onClick={() => setCreateOpen(true)}
            >
              Create Cognitory
            </button>
          ) : null}
        </div>

        {createOpen ? (
          <CreateCognitoryPanel
            onClose={() => setCreateOpen(false)}
            onSuccess={() => registry.retry()}
          />
        ) : null}

        {registry.systems.length > 0 ? (
          <ul
            data-testid="platform-admin-cogs-list"
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "grid",
              gap: "0.75rem",
            }}
          >
            {registry.systems.map(entry => (
              <SystemSummaryRow
                key={entry.systemId}
                systemId={entry.systemId}
                displayName={entry.displayName}
                status={entry.status}
                enterable={entry.enterable}
                canOpen={entry.canOpen}
                canSetup={entry.canSetup}
                initialOwnerAssignmentStatus={
                  entry.initialOwnerAssignmentStatus
                }
                onRegistryRefresh={registry.retry}
              />
            ))}
          </ul>
        ) : null}
        <ReturnHomeLink />
      </div>
    </AdminCogsPageShell>
  );
}
